import type { SurveyRoundDto } from "../lib/domain/surveyRound/SurveyRoundDto";
import type { I18Language } from "./I18Language";
import type { SegmentLabel } from "./Organization";
import type {
	CalculationMethods,
	CommentResultSource,
	ValueResultSource,
} from "./Report";

export interface CommentSegment {
	id: string;
	name: string;
	attributeName?: string;
	labels?: SegmentLabel[];
}

export type SingleComment = {
	shadowId: string;
	id: string;
	text: string;
	roundDate: string;
	roundId: string;
	valueId: string;
	surveyId: string;
	sentiment?: SurveyAnswerCommentSentiment;
	canFollowUp?: boolean;
	totalFollowUps?: number;
	/**
	 * accessible follow up ids for the current user
	 */
	followUpIds?: string[];
	sourceLanguage?: string;
	semanticDistance?: number;
	attachments?: string[];
	segments?: CommentSegment[];
} & (
	| {
			source: "peer-feedback";
			templateId: string;
	  }
	| {
			source: "survey";
	  }
);

export interface SurveyAnswerCommentSentiment {
	sourceLanguage?: string;
	result: never | null;
	rating?: SentimentRating;
}

export enum SentimentRating {
	neutral = "neutral",
	mixed = "mixed",
	negative = "negative",
	veryNegative = "very-negative",
	positive = "positive",
	veryPositive = "very-positive",
}

export interface CommentRequestResult {
	request?: CommentResultRequest;
	comments: SingleComment[];
	availableComments: number;
	questions?: RequestResultQuestionDto[];
	attachments?: { id: string; count: number }[];
	rounds: SurveyRoundDto[];
}

export interface CommentResultRequest {
	resultId: string;
	sources?: CommentResultSource[];
	groupBy: GroupByOptions;
	/* LEGACY */
	surveyIds?: string[];
	valueIds?: string[];
	/* #### */
	/**
	 * format: attribute.segment
	 */
	attributeSegment?: string;
}

export interface ValueResultRequest {
	resultId: string;
	groupBy: GroupByOptions;
	/* LEGACY */
	surveyIds?: string[];
	valueIds?: string[];
	/* #### */
	sources?: ValueResultSource[];
	/**
	 * format: attribute.segment
	 */
	attributeSegment?: string;
	method: CalculationMethods;
	histogramBuckets?: number[];
	npsLimits?: [number, number];
}

export interface ValueResult extends Omit<RawValueResult, "_id"> {
	roundId: string;
}

export type RequestResultQuestionDto = {
	valueId: string;
	surveyId: string;
	text: I18Language;
	text2?: I18Language;
	prefix?: string;
	type: "value" | "comment";
	surveyName: string;
} & (
	| {
			source: "survey";
	  }
	| {
			source: "peer-feedback";
			templateId: string;
			groupId: string;
			groupName: I18Language;
			templateName: I18Language;
	  }
);

export interface ValuesRequestResult {
	request: ValueResultRequest;
	questions?: RequestResultQuestionDto[];
	values: ValueResult[];
}

export type ResultId = string;

export interface ResultDto {
	segmentPath: string; // added by reducer
	rounds: SurveyRoundDto[];
	values: ResultValues;
	comments: ResultComments;
	dataSets?: ResultDataSets;
	multipleChoice: ResultMultipleChoices;
}

export type EnrichedResult = ResultDto & {
	title: string;
	labels: SegmentLabel[];
};

export type ResultValues = Record<ResultId, ValuesRequestResult>;
export type ResultComments = Record<ResultId, CommentRequestResult>;
export type ResultDataSets = Record<ResultId, DataSetResultDto>;
export type ResultMultipleChoices = Record<
	ResultId,
	PopulatedMultipleChoiceResultDto
>;

export type GroupByOptions =
	| "round"
	| "day"
	| "week"
	| "month"
	| "quarter"
	| "year";
export interface DataSetResultDto {
	total: number;
	request: never;
	data: DataSetResultData[] | null;
	groupBy: GroupByOptions;
	questions: RequestResultQuestionDto[];
}

/**
 * can be stringified date or roundId
 */
export type BucketKey = string;

export interface DataSetResultData {
	id: string;
	bucketKey: BucketKey;
	visibleId: string | null;
	values: Record<string, null | number>;
	comments: Record<string, null | string> | null;
	valueRanges: Record<string, number>;
}

/* ############### RAW Results ############## */
export interface RawValueResult {
	_id: string;
	request?: ValueResultRequest;
	roundDate: string;
	numberOfRoundAnswers?: number | null | undefined;
	avg: number | null;
	count: number | null;
	promotors: number | null;
	neutrals: number | null;
	detractors: number | null;
	histogram?: Record<string, number | null | undefined>;
	isCombinedResult: boolean;
	localValueStd: number;
	localValueMin: number;
	localValueMax: number;
	localValueMedian: number;
}
export interface RawValuesRequestResult {
	request: ValueResultRequest;
	questions: RequestResultQuestionDto[];
	values: RawValueResult[];
}

export interface RawResult {
	segmentPath: string; // added by reducer
	rounds: SurveyRoundDto[];
	values: Record</* resultId */ string, RawValuesRequestResult>;
	comments: Record<string, CommentRequestResult>;
}

export type MultipleChoiceValueId = string;

export interface PopulatedMultipleChoiceResultDto {
	request: unknown;
	results: MultipleChoiceRoundResultDto[];
	questions: RequestResultQuestionDto[];
}

export interface MultipleChoiceRoundResultDto {
	_id: string;
	/**
	 * Total of submitted answers
	 */
	total: number;
	values: Record<MultipleChoiceValueId, MultipleChoiceResultValue>;
}

export interface MultipleChoiceResultValue {
	id: MultipleChoiceValueId;
	count: number | null;
	percent: number | null;
}
